import React from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep this loud in dev, but show a friendly UI in prod.
    // eslint-disable-next-line no-console
    console.error('[UI ErrorBoundary]', error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      // Recover automatically when navigating to a different screen.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    const { hasError, error } = this.state;
    if (!hasError) return this.props.children;

    return (
      <Box sx={{ py: 2 }}>
        <Alert
          severity="error"
          variant="outlined"
          sx={(theme) => ({
            borderRadius: 3,
            borderColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.55 : 0.35),
            backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
          })}
        >
          <AlertTitle>Something went wrong</AlertTitle>

          <Typography variant="body2" sx={{ mb: 1 }}>
            This screen failed to render. Try reloading. If it keeps happening, check the browser
            console and `backend/storage/logs/laravel.log`.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button variant="outlined" onClick={() => window.history.back()}>
              Go back
            </Button>
          </Stack>
        </Alert>

        {process.env.NODE_ENV === 'development' && error ? (
          <Box
            component="pre"
            sx={(theme) => ({
              mt: 2,
              p: 2,
              borderRadius: 3,
              overflow: 'auto',
              fontSize: 12,
              backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.18 : 0.04),
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            {error?.stack || String(error)}
          </Box>
        ) : null}
      </Box>
    );
  }
}

export default ErrorBoundary;

